/**
 * Rider Fee API
 * 라이더별 수수료 관리 API (협력사가 라이더에게 부과)
 */
import { apiClient } from "@/shared/api/client";

const BASE_PATH = "/api/v1/supplier-rider-fees";

/** 라이더 수수료 정보 응답 DTO */
export interface RiderFeeDto {
  riderId: string;
  riderName: string;
  commissionType: string; // PER_CASE_FIXED, PER_CASE_RATE, PER_DAY_FIXED, PER_DAY_RATE
  amount: number;
  applyDate: string; // yyyy-MM-dd 형식
  applyTiming: string; // IMMEDIATE, NEXT_DAY
  contStatus: string;
}

/** 라이더 수수료 저장 요청 DTO */
export interface SaveRiderFeeRequest {
  suppId: string;
  riderId: string;
  commissionType: string;
  amount: number;
  applyTiming: string;
  userId: string;
}

/** 라이더 수수료 변경 이력 DTO */
export interface RiderFeeHistoryDto {
  applyDate: string;
  commissionType: string; // 한글명
  amount: number;
  modifier: string;
  insDate?: string;
}

/** 라이더 수수료 저장 응답 DTO */
export interface SaveRiderFeeResponse {
  riderId: string;
  suppId: string;
  applyStartDate: string; // yyyy-MM-dd 형식
  isImmediateApply: boolean; // 수요일에 설정한 경우 true
}

/** 라이더 수수료 일괄 저장 응답 DTO */
export interface BulkSaveRiderFeeResponse {
  totalCount: number;
  successCount: number;
  failCount: number;
  applyStartDate: string; // yyyy-MM-dd 형식
  isImmediateApply: boolean; // 수요일에 설정한 경우 true
}

/**
 * Rider Fee API 서비스
 */
export const riderFeeApi = {
  /**
   * 협력사별 라이더 수수료 목록 조회
   * GET /api/v1/supplier-rider-fees?suppId={suppId}
   */
  getRiderFees: async (suppId: string): Promise<RiderFeeDto[]> => {
    const response = await apiClient.get<RiderFeeDto[]>(BASE_PATH, {
      params: { suppId },
    });
    return response.data;
  },

  /**
   * 라이더 수수료 저장
   * POST /api/v1/supplier-rider-fees
   */
  saveRiderFee: async (request: SaveRiderFeeRequest): Promise<SaveRiderFeeResponse> => {
    const response = await apiClient.post<SaveRiderFeeResponse>(BASE_PATH, request);
    return response.data;
  },

  /**
   * 라이더 수수료 일괄 저장
   * POST /api/v1/supplier-rider-fees/bulk-save
   */
  saveRiderFeesBatch: async (requests: SaveRiderFeeRequest[]): Promise<BulkSaveRiderFeeResponse> => {
    const response = await apiClient.post<BulkSaveRiderFeeResponse>(`${BASE_PATH}/bulk-save`, requests);
    return response.data;
  },

  /**
   * 라이더 수수료 변경 이력 조회
   * GET /api/v1/supplier-rider-fees/{riderId}/history?suppId={suppId}
   */
  getRiderFeeHistory: async (
    suppId: string,
    riderId: string,
  ): Promise<RiderFeeHistoryDto[]> => {
    const response = await apiClient.get<RiderFeeHistoryDto[]>(
      `${BASE_PATH}/${riderId}/history`,
      { params: { suppId } },
    );
    return response.data;
  },
};
